// ============================================================================
// PORTAL VAULT REPOSITORY
// ARQUIVO: src/services/PortalVaultRepository.js
// RESPONSABILIDADE: Operações Firestore do PortalVault (documentos, acks, processos, workspaces).
// Criado: Fase 6.3 — mover Firestore direto de PortalVault.jsx para repositório.
// ============================================================================

import {
  addDoc, collection, deleteDoc, doc, getDocs, limit, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where
} from 'firebase/firestore';
import { db } from '../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../config/Config';
import { FS } from '../shared/constants/firestorePaths';

const APP_ID = FIREBASE_CONFIG?.projectId || 'portalrda-f312d';

/** Deleta documento (revogação). */
export async function deleteDocument(docId) {
  await deleteDoc(doc(db, ...FS.documents(APP_ID), docId));
}

/** Busca IDs de processos (autos_workspaces) vinculados ao cliente.
 * Fusão Processos/Caderno (MC29): mesmo redirecionamento do MC28
 * (PortalProntuarioRepository.js tem uma função quase idêntica — dívida
 * técnica registrada no Pré-Voo, não consolidada aqui, fora de escopo). */
export async function fetchClientProcessIds(clientId) {
  const merged = new Set();
  const refs = [];
  if (String(APP_ID || '').trim()) refs.push(collection(db, ...FS.autosWorkspaces(APP_ID)));

  for (const ref of refs) {
    const tasks = [];
    if (clientId) {
      tasks.push(getDocs(query(ref, where('clientId', '==', clientId), limit(50))));
      tasks.push(getDocs(query(ref, where('partiesRefIds', 'array-contains', clientId), limit(50))));
    }
    const results = await Promise.allSettled(tasks);
    results.forEach((result) => {
      if (result.status !== 'fulfilled') return;
      result.value.docs.forEach((d) => {
        const pid = String(d.id || '').trim();
        if (pid) merged.add(pid);
      });
    });
  }
  return Array.from(merged);
}

/** Busca workspaces do cliente (getDocs). */
export async function fetchClientWorkspaces(clientId) {
  const ref = collection(db, 'artifacts', APP_ID, 'public', 'data', 'autos_workspaces');
  const snap = await getDocs(query(ref, where('clientId', '==', clientId), limit(120)));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
}

/** Observa workspaces do cliente. */
export function watchClientWorkspaces(clientId, onData, onError) {
  const ref = collection(db, 'artifacts', APP_ID, 'public', 'data', 'autos_workspaces');
  return onSnapshot(
    query(ref, where('clientId', '==', clientId), limit(120)),
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))),
    onError
  );
}

/** Busca GED links do cliente (getDocs). */
export async function fetchClientGedLinks(clientId) {
  const snap = await getDocs(query(collection(db, 'rda_ged_links'), where('partiesRefs', 'array-contains', clientId), limit(250)));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
}

/** Observa GED links do cliente. */
export function watchClientGedLinks(clientId, onData, onError) {
  return onSnapshot(
    query(collection(db, 'rda_ged_links'), where('partiesRefs', 'array-contains', clientId), limit(250)),
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))),
    onError
  );
}

/** Busca documentos do cliente (getDocs). */
export async function fetchClientDocuments(clientId) {
  const docsRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'documents');
  const snap = await getDocs(query(docsRef, where('clientId', '==', clientId), limit(200)));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
}

/** Observa documentos do cliente (Phase 3 ReBAC). */
export function watchClientDocuments(clientId, onData, onError) {
  const docsRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'documents');
  const qq = query(docsRef, where('clientId', '==', clientId), limit(200));
  return onSnapshot(qq, (snap) => {
    onData(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
  }, onError);
}

/** Observa documentos legacy. */
export function watchLegacyDocuments(clientId, onData, onError) {
  const docsRef = collection(db, 'documents');
  const qq = query(docsRef, where('clientId', '==', clientId), limit(200));
  return onSnapshot(qq, (snap) => {
    onData(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
  }, onError);
}

/** Busca acknowledgements do cliente (getDocs). */
export async function fetchDocumentAcknowledgements(clientId) {
  const ref = collection(db, 'artifacts', APP_ID, 'public', 'data', 'document_acknowledgements');
  const snap = await getDocs(query(ref, where('clientId', '==', clientId), limit(500)));
  const out = {};
  snap.docs.forEach((d) => {
    const data = d.data() || {};
    const docId = String(data?.documentId || '').trim();
    if (docId) out[docId] = true;
  });
  return out;
}

/** Observa acknowledgements do cliente. */
export function watchDocumentAcknowledgements(clientId, onData, onError) {
  const ref = collection(db, 'artifacts', APP_ID, 'public', 'data', 'document_acknowledgements');
  const qq = query(ref, where('clientId', '==', clientId), limit(500));
  return onSnapshot(qq, (snap) => {
    const out = {};
    snap.docs.forEach((d) => {
      const data = d.data() || {};
      const docId = String(data?.documentId || '').trim();
      if (docId) out[docId] = true;
    });
    onData(out);
  }, onError);
}

/** Observa acknowledgements legacy. */
export function watchLegacyAcknowledgements(clientId, onData, onError) {
  const ref = collection(db, 'document_acknowledgements');
  const qq = query(ref, where('clientId', '==', clientId), limit(500));
  return onSnapshot(qq, (snap) => {
    const out = {};
    snap.docs.forEach((d) => {
      const data = d.data() || {};
      const docId = String(data?.documentId || '').trim();
      if (docId) out[docId] = true;
    });
    onData(out);
  }, onError);
}

/** Escreve acknowledgement. */
export async function writeDocumentAcknowledgement(ackId, data) {
  const ackCol = collection(db, 'artifacts', APP_ID, 'public', 'data', 'document_acknowledgements');
  await setDoc(doc(ackCol, ackId), { ...data, viewedAt: serverTimestamp() }, { merge: true });
}

/** Escreve acknowledgement legacy. */
export async function writeLegacyAcknowledgement(ackId, data) {
  const ackCol = collection(db, 'document_acknowledgements');
  await setDoc(doc(ackCol, ackId), { ...data, viewedAt: serverTimestamp() }, { merge: true });
}

/** Adiciona nota de cliente (documento visualizado). */
export async function addClientViewNote(clientId, noteData) {
  await addDoc(collection(db, 'artifacts', APP_ID, 'public', 'data', 'clients', clientId, 'notes'), { ...noteData, createdAt: serverTimestamp() });
}

/** Atualiza documento com dados de assinatura. */
export async function updateDocumentSignature(docId, data) {
  await updateDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'documents', docId), { ...data, updatedAt: serverTimestamp() });
}

/** Subscription completa de documentos (Phase 3 ReBAC). */
export function subscribeClientDocuments(clientId, onData, onError) {
  const docsRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'documents');
  const qq = query(docsRef, where('clientId', '==', clientId), limit(200));
  return onSnapshot(qq, (snap) => {
    onData(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
  }, onError);
}

/** Subscription completa de documentos legacy. */
export function subscribeLegacyDocuments(clientId, onData, onError) {
  const docsRef = collection(db, 'documents');
  const qq = query(docsRef, where('clientId', '==', clientId), limit(200));
  return onSnapshot(qq, (snap) => {
    onData(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
  }, onError);
}

/** Subscription completa de acknowledgements. */
export function subscribeDocumentAcknowledgements(clientId, onData, onError) {
  const ref = collection(db, 'artifacts', APP_ID, 'public', 'data', 'document_acknowledgements');
  const qq = query(ref, where('clientId', '==', clientId), limit(500));
  return onSnapshot(qq, (snap) => {
    const out = {};
    snap.docs.forEach((d) => {
      const data = d.data() || {};
      const docId = String(data?.documentId || '').trim();
      if (docId) out[docId] = true;
    });
    onData(out);
  }, onError);
}

/** Subscription completa de acknowledgements legacy. */
export function subscribeLegacyAcknowledgements(clientId, onData, onError) {
  const ref = collection(db, 'document_acknowledgements');
  const qq = query(ref, where('clientId', '==', clientId), limit(500));
  return onSnapshot(qq, (snap) => {
    const out = {};
    snap.docs.forEach((d) => {
      const data = d.data() || {};
      const docId = String(data?.documentId || '').trim();
      if (docId) out[docId] = true;
    });
    onData(out);
  }, onError);
}
